import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonicModule, IonRefresher } from '@ionic/angular';
import { TournamentHeaderComponent } from '../tournament-header/tournament-header.component';
import { Tournament } from '../../../_shared/models/tournament.model';
import { map, merge, of, Subject, switchMap, tap, iif } from 'rxjs';
import { TournamentDetailsService } from '../tournament-details.service';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Socket } from 'ngx-socket-io';
import { SocketService } from '../../../_core/services/socket.service';
import { AuthService } from '../../../auth/auth.service';
import { Preferences } from '@capacitor/preferences';

@Component({
  selector: 'app-standings',
  templateUrl: './standings.component.html',
  styleUrls: ['./standings.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    TournamentHeaderComponent,
  ]
})
export class StandingsComponent implements OnInit, OnDestroy {
  private tournamentDetailsService = inject(TournamentDetailsService);
  private activatedRoute = inject(ActivatedRoute);
  private socket = inject(Socket);
  private socketService = inject(SocketService);

  @ViewChild('refresher') refresher: IonRefresher;

  authService = inject(AuthService);

  tournament: Tournament;
  sendStandings = false;

  getTournamentMatches$ = new Subject<void>();
  getParticipants$ = new Subject<void>();

  matchIds: string[];
  deviceId: string;

  tournamentMatches$ = this.getTournamentMatches$.pipe(
    switchMap(() =>
      this.tournamentDetailsService.getMatches(this.tournament._id, 'all', null, false)
    ),
    map(res => res.data),
    tap(matches => {
      // Join rooms for live score updates for each match
      matches.forEach(match => {
        this.socket.emit('join-room', `match/${match._id}/score`);
      });

      this.matchIds = matches.map(m => m._id);
    }),
    switchMap(matches => {
      // Initially calculate standings
      const standings = this.calculateStandings(matches);

      // Listen for live score updates from the socket
      const liveScoreUpdates$ = this.socketService.getSocketUpdate('match/score').pipe(
        // Each time a live score update comes in, update the match data and recalculate standings
        map((liveUpdate: any) => {
          const updatedMatchIndex = matches.findIndex(match => match._id === liveUpdate._id);
          if (updatedMatchIndex !== -1 && (!this.deviceId || !liveUpdate.deviceId || liveUpdate.deviceId !== this.deviceId)) {
            // Update the score of the match
            matches[updatedMatchIndex].score = liveUpdate.score;
          }

          // Recalculate standings after receiving the score update
          return this.calculateStandings(matches);
        })
      );

      // Combine the initial standings and live updates (so it starts with initial data and continues updating with socket updates)
      return merge(of(standings), liveScoreUpdates$);
    }),
    tap(() => this.refresher.complete()),
    tap((standings) => {
      if (this.sendStandings) {
        this.tournamentDetailsService.sendStandings(this.tournament._id, standings, 'ross').subscribe({
          next: () => {
            //console.log('Standings updated successfully')
          },
          error: (err) => {
            //console.error('Error updating standings:', err)
          }
        });
      }
    })
  );

  // Helper function to calculate standings
  private calculateStandings(matches: any[]): { name: string, points: number }[] {
    const pointsMap: { [participantId: string]: { name: string; points: number } } = {};

    matches.forEach(match => {
      match.sides.forEach((side, i) => {
        const participantId = side.participant.participantId;
        const displayName = side.participant.displayName;

        if (!pointsMap[participantId]) {
          pointsMap[participantId] = { name: displayName, points: 0 };
        }

        pointsMap[participantId].points += match.score ? Number(match.score[`side${i + 1}PointScore`]) : 0;
      });
    });

    // Convert points map to standings array and sort by points
    const standings = Object.values(pointsMap);
    standings.sort((a, b) => b.points - a.points);

    return standings;
  }

  standings = toSignal(this.tournamentMatches$)


  constructor() { }

  async ngOnInit() {
    const tournamentId = this.activatedRoute.snapshot.parent.params.id;
    this.tournament = await this.tournamentDetailsService.getTournamentInfo(tournamentId);
    this.deviceId = (await Preferences.get({ key: 'deviceId' }))?.value || '';
    this.getTournamentMatches$.next();

  }

  ngOnDestroy() {
    this.matchIds.forEach(id => {
      this.socketService.leaveRoomAndRemoveSocketListener(`match/${id}/score`);
    });
  }

  updateSendStandingsStatus() {
    this.sendStandings = !this.sendStandings;
    if (this.sendStandings) {
      this.getTournamentMatches$.next();
    }
  }

  doRefresh() {
    this.getTournamentMatches$.next();
  }

}
